import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { setAccessToken, BASE_BACKEND_URL } from '../../auth'
import { useAuth } from '../../auth/AuthContext'
import { saveSessionCookies } from '../../auth/sessionCookies'
import { TIMED_TOAST_ALERT_DURATION_MS } from '../../components/alerts/TimedToastAlert'
import { GlobalContext } from '../../global_context/GlobalContext'
import { setUpTaxSettings } from '../../helpers/utils'
import {
  getLoginAlertMessage,
  getLoginErrorState,
  LOGIN_ALERTS,
  LoginState
} from '../constants/loginStates'
import { clearMfaToken, loadMfaToken } from '../mfaStorage'

export default function useMfaLoginPage() {
  const [otpCode, setOtpCode] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  const [rememberDevice, setRememberDevice] = useState(true)
  const [verificationMethod, setVerificationMethod] = useState(
    () => loadMfaToken()?.mfa_method || null
  )
  const [emailCodeSent, setEmailCodeSent] = useState(() => loadMfaToken()?.mfa_method === 'email')
  const [resendBusy, setResendBusy] = useState(false)
  const [loginState, setLoginState] = useState(LoginState.NONE)
  const [alertId, setAlertId] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { applyLoginPayload } = useAuth()
  const { syncUserRole } = useContext(GlobalContext)
  const navigate = useNavigate()
  const mfaChallenge = loadMfaToken()
  const rememberDeviceAvailable = Boolean(mfaChallenge?.remember_device_available)
  const rememberDeviceDays = mfaChallenge?.remember_device_days || 0
  const mfaMethods = Array.isArray(mfaChallenge?.mfa_methods) ? mfaChallenge.mfa_methods : []
  const totpAvailable = mfaMethods.includes('totp')
  const emailAvailable = mfaMethods.includes('email')
  const destinationHint = mfaChallenge?.destination_hint || ''

  const showLoginAlert = (nextState) => {
    setLoginState(nextState)
    setAlertId(Date.now())
  }

  const clearLoginAlert = () => {
    setLoginState(LoginState.NONE)
  }

  useEffect(() => {
    if (!mfaChallenge) {
      navigate('/', { replace: true })
    }
  }, [mfaChallenge, navigate])

  useEffect(() => {
    if (loginState === LoginState.NONE) return undefined

    const dismissTimer = window.setTimeout(() => {
      setLoginState(LoginState.NONE)
    }, TIMED_TOAST_ALERT_DURATION_MS)

    return () => window.clearTimeout(dismissTimer)
  }, [loginState, alertId])

  const finishLogin = (data) => {
    setAccessToken(data.token)
    applyLoginPayload(data)
    saveSessionCookies(data)
    syncUserRole?.()
    setUpTaxSettings(data.intl_locale)
    clearMfaToken()
    navigate('/inicio', { replace: true })
  }

  const verifyMfa = (event) => {
    event.preventDefault()
    const trimmedCode = useRecoveryCode ? recoveryCode.trim() : otpCode.trim()
    if (!mfaChallenge?.mfa_token || !verificationMethod) return
    if (useRecoveryCode ? !trimmedCode : !/^\d{6}$/.test(trimmedCode)) return

    clearLoginAlert()
    setIsSubmitting(true)

    fetch(`${BASE_BACKEND_URL}/login/mfa`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        mfa_token: mfaChallenge.mfa_token,
        code: trimmedCode,
        method: useRecoveryCode ? 'totp' : verificationMethod,
        remember_device: rememberDeviceAvailable && rememberDevice
      })
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          const nextState = getLoginErrorState(response.status, data.reason)
          showLoginAlert(nextState)
          if (data.reason === 'mfa_token_expired') {
            clearMfaToken()
          }
          return
        }

        finishLogin(data)
      })
      .catch(() => {
        showLoginAlert(LoginState.GENERIC_ERROR)
      })
      .finally(() => setIsSubmitting(false))
  }

  const backToCredentials = () => {
    clearMfaToken()
    navigate('/', { replace: true })
  }

  const toggleRecoveryCode = () => {
    setUseRecoveryCode((current) => !current)
    setVerificationMethod('totp')
    setOtpCode('')
    setRecoveryCode('')
    clearLoginAlert()
  }

  const useAuthenticator = () => {
    setVerificationMethod('totp')
    setUseRecoveryCode(false)
    setOtpCode('')
    setRecoveryCode('')
    clearLoginAlert()
  }

  const useEmailCode = () => {
    if (emailCodeSent) {
      setVerificationMethod('email')
      setUseRecoveryCode(false)
      setOtpCode('')
      setRecoveryCode('')
      clearLoginAlert()
      return
    }
    resendEmailCode(true)
  }

  const resendEmailCode = (selectEmail = false) => {
    if (!mfaChallenge?.mfa_token || resendBusy) return

    clearLoginAlert()
    setResendBusy(true)
    fetch(`${BASE_BACKEND_URL}/login/mfa/resend`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ mfa_token: mfaChallenge.mfa_token })
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          showLoginAlert(getLoginErrorState(response.status, data.reason))
          if (data.reason === 'mfa_token_expired') {
            clearMfaToken()
          }
          return
        }

        setEmailCodeSent(true)
        if (selectEmail) {
          setVerificationMethod('email')
          setUseRecoveryCode(false)
          setOtpCode('')
          setRecoveryCode('')
        } else {
          showLoginAlert(LoginState.MFA_CODE_RESENT)
        }
      })
      .catch(() => {
        showLoginAlert(LoginState.GENERIC_ERROR)
      })
      .finally(() => setResendBusy(false))
  }

  const handleOtpCodeChange = (value) => {
    setOtpCode(value)
    if (loginState !== LoginState.NONE) {
      clearLoginAlert()
    }
  }

  const handleRecoveryCodeChange = (value) => {
    setRecoveryCode(value)
    if (loginState !== LoginState.NONE) {
      clearLoginAlert()
    }
  }

  const alertMessage = getLoginAlertMessage(loginState)
  const alertSeverity = LOGIN_ALERTS[loginState]?.variant || 'error'

  return {
    otpCode,
    setOtpCode: handleOtpCodeChange,
    recoveryCode,
    setRecoveryCode: handleRecoveryCodeChange,
    useRecoveryCode,
    toggleRecoveryCode,
    rememberDevice,
    setRememberDevice,
    rememberDeviceAvailable,
    rememberDeviceDays,
    verificationMethod,
    emailCodeSent,
    destinationHint,
    totpAvailable,
    emailAvailable,
    resendBusy,
    useAuthenticator,
    useEmailCode,
    resendEmailCode,
    isSubmitting,
    verifyMfa,
    backToCredentials,
    alertId,
    formAlertMessage: alertMessage,
    formAlertSeverity: alertSeverity,
    clearLoginAlert
  }
}
